import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, X, Search, Phone, Mic, MicOff, Pause, ArrowRightLeft, PhoneOff,
  CalendarClock, PhoneCall, Bot, User as UserIcon, Tag, ListChecks, CheckCircle2,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { AI_AGENTS, CALL_PURPOSES, HUMAN_AGENTS, MOCK_CONTACTS } from "../data";
import { CallContact, CallStatus, HandlerType, LiveCallRow, TranscriptMessage } from "../types";

type Step = "contact" | "method" | "handler" | "context" | "review" | "live" | "completed";

const STEP_TITLES: Record<Step, { title: string; subtitle: string }> = {
  contact: { title: "Start a Call", subtitle: "Select a contact to call directly from ConvoHub." },
  method: { title: "Choose Call Method", subtitle: "Choose how you want to reach the contact." },
  handler: { title: "Choose Who Handles the Call", subtitle: "Select the agent or AI who will handle the call." },
  context: { title: "Add Call Context", subtitle: "Add purpose and notes to help the agent/AI." },
  review: { title: "Review and Start Call", subtitle: "Review the details and start the call." },
  live: { title: "Live Call in Progress", subtitle: "Monitor the call in real time." },
  completed: { title: "Call Completed", subtitle: "Add outcome and notes." },
};

const OUTCOMES = ["Interested", "Not Interested", "Callback", "No Answer", "Follow-up"];

const fmtTimer = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const SelectableCard: React.FC<{
  selected: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ selected, onClick, icon, title, description }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full flex items-start gap-3 text-left p-3.5 rounded-lg border transition-colors ${
      selected
        ? "border-indigo-500 ring-1 ring-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
        : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
    }`}
  >
    <span className="w-9 h-9 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 flex items-center justify-center shrink-0">{icon}</span>
    <span className="min-w-0">
      <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{title}</span>
      <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</span>
    </span>
    <span className={`ml-auto mt-1 w-4 h-4 rounded-full border-2 shrink-0 ${selected ? "border-indigo-600 bg-indigo-600" : "border-gray-300 dark:border-gray-600"}`} />
  </button>
);

const ContactSummaryCard: React.FC<{ contact: CallContact; badge?: React.ReactNode }> = ({ contact, badge }) => (
  <div className="flex items-center gap-3 p-3.5 rounded-lg border border-gray-200 dark:border-gray-700">
    <span className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-gray-700 shrink-0 ${contact.avatarColor}`}>
      {contact.initial}
    </span>
    <div className="min-w-0 flex-1">
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{contact.name}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{contact.phone}</div>
      {contact.lastContacted && <div className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">{contact.lastContacted}</div>}
    </div>
    {badge}
  </div>
);

const SAMPLE_LIVE_TRANSCRIPT: TranscriptMessage[] = [
  { speaker: "agent", name: "AI Agent", text: "Hi! This is ConvoHub calling on your recent enquiry. How can I help you today?", time: "" },
  { speaker: "contact", name: "Contact", text: "Hi, I wanted to know more about the premium plan.", time: "" },
  { speaker: "agent", name: "AI Agent", text: "Sure! I can share the details and pricing with you right now.", time: "" },
];

export const StartCallModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onCallCompleted: (row: LiveCallRow) => void;
}> = ({ isOpen, onClose, onCallCompleted }) => {
  const [step, setStep] = useState<Step>("contact");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<"all" | "recent" | "mine">("all");
  const [contactId, setContactId] = useState<string | null>(null);
  const [method, setMethod] = useState<"whatsapp" | "phone" | null>(null);
  const [handlerType, setHandlerType] = useState<HandlerType | null>(null);
  const [humanAgentId, setHumanAgentId] = useState(HUMAN_AGENTS[0].id);
  const [aiAgentId, setAiAgentId] = useState(AI_AGENTS[0].id);
  const [purpose, setPurpose] = useState(CALL_PURPOSES[0]);
  const [instructions, setInstructions] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [outcome, setOutcome] = useState(OUTCOMES[0]);
  const [notes, setNotes] = useState("");

  // Reset the whole wizard whenever it's reopened for a fresh call.
  useEffect(() => {
    if (isOpen) {
      setStep("contact"); setSearch(""); setTab("all"); setContactId(null); setMethod(null);
      setHandlerType(null); setPurpose(CALL_PURPOSES[0]); setInstructions(""); setSeconds(0);
      setOutcome(OUTCOMES[0]); setNotes("");
    }
  }, [isOpen]);

  // Live call timer.
  useEffect(() => {
    if (step !== "live") return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [step]);

  const contact = useMemo(() => MOCK_CONTACTS.find((c) => c.id === contactId) || null, [contactId]);
  const aiAgent = useMemo(() => AI_AGENTS.find((a) => a.id === aiAgentId)!, [aiAgentId]);
  const humanAgent = useMemo(() => HUMAN_AGENTS.find((a) => a.id === humanAgentId)!, [humanAgentId]);

  const filteredContacts = useMemo(() => {
    let list = MOCK_CONTACTS;
    if (tab === "recent") list = list.filter((c) => c.lastContacted && !c.lastContacted.includes("Never"));
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((c) => c.name.toLowerCase().includes(q) || c.phone.includes(q));
    return list;
  }, [tab, search]);

  const handlerLabel = handlerType === "myself" ? "You" : handlerType === "human" ? humanAgent.name : handlerType === "ai" ? aiAgent.name : "";
  const handlerType_ = handlerType === "ai" ? "ai" : handlerType === "human" ? "human" : "you";

  const goBack = (to: Step) => setStep(to);

  const startCall = () => { setSeconds(0); setStep("live"); };

  const endCall = () => {
    setStep("completed");
    setNotes(`Customer is interested in the ${purpose.toLowerCase()}. Requested pricing details and demo.`);
  };

  const saveAndClose = () => {
    if (!contact) return;
    const row: LiveCallRow = {
      id: `lc-${Date.now()}`,
      from: contact.phone,
      to: contact.phone,
      channel: method || "whatsapp",
      agentName: handlerLabel,
      agentType: handlerType_ as "human" | "ai" | "you",
      startTime: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      status: "Call Ended" as CallStatus,
      duration: fmtTimer(seconds),
      hasTranscript: true,
      transcript: SAMPLE_LIVE_TRANSCRIPT,
      purpose,
      summary: notes,
      intent: "Product Enquiry",
      sentiment: "Positive",
    };
    onCallCompleted(row);
    toast.success("Call logged and added to Live Calls.");
    onClose();
  };

  if (!isOpen) return null;

  const wide = step === "review" || step === "live";
  const { title, subtitle } = STEP_TITLES[step];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/50" onClick={step === "live" ? undefined : onClose} />
      <div
        className={`relative w-full ${wide ? "max-w-3xl" : "max-w-md"} max-h-[92vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col overflow-hidden transition-all`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 pt-4 sm:pt-5 pb-3 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div className="min-w-0">
            {step !== "contact" && step !== "live" && step !== "completed" && (
              <button
                type="button"
                onClick={() => goBack(step === "method" ? "contact" : step === "handler" ? "method" : step === "context" ? "handler" : "context")}
                className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-1.5"
              >
                <ArrowLeft size={13} /> Back
              </button>
            )}
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</h2>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
          </div>
          {step !== "live" && (
            <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-[#7B7B7B] font-semibold hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0">
              <X size={18} />
            </button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Step 1 — Select a Contact */}
          {step === "contact" && (
            <div className="space-y-4">
              <div className="relative">
                <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or phone number"
                  className="w-full pl-9 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex gap-1 border-b border-gray-100 dark:border-gray-800">
                {([["all", "All Contacts"], ["recent", "Recent"], ["mine", "Assigned to me"]] as const).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setTab(key)}
                    className={`px-3 py-2 text-xs sm:text-sm font-medium border-b-2 -mb-px transition-colors ${
                      tab === key ? "border-indigo-600 text-indigo-600 dark:text-indigo-400" : "border-transparent text-gray-500 dark:text-gray-400"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {filteredContacts.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No contacts match your search.</p>
                ) : (
                  filteredContacts.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setContactId(c.id)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-colors text-left ${
                        contactId === c.id ? "border-indigo-500 ring-1 ring-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10" : "border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800"
                      }`}
                    >
                      <span className={`w-4 h-4 rounded-full border-2 shrink-0 ${contactId === c.id ? "border-indigo-600 bg-indigo-600" : "border-gray-300 dark:border-gray-600"}`} />
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold text-gray-700 shrink-0 ${c.avatarColor}`}>{c.initial}</span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                          {c.name}
                          {c.channel === "whatsapp" ? <FaWhatsapp className="text-green-500 shrink-0" size={12} /> : <Phone className="text-indigo-500 shrink-0" size={12} />}
                        </span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{c.phone}</span>
                      </span>
                      {c.tag && <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap ${c.tagClassName}`}>{c.tag}</span>}
                    </button>
                  ))
                )}
              </div>

              <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Showing 1-{filteredContacts.length} of {MOCK_CONTACTS.length} contacts</p>
            </div>
          )}

          {/* Step 2 — Choose Call Method */}
          {step === "method" && contact && (
            <div className="space-y-4">
              <ContactSummaryCard contact={contact} />
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">How would you like to call?</h3>
                <div className="space-y-2.5">
                  <SelectableCard
                    selected={method === "whatsapp"}
                    onClick={() => setMethod("whatsapp")}
                    icon={<FaWhatsapp className="text-green-500" size={16} />}
                    title="WhatsApp Call"
                    description="Call using the connected WhatsApp number"
                  />
                  <SelectableCard
                    selected={method === "phone"}
                    onClick={() => setMethod("phone")}
                    icon={<Phone className="text-indigo-500" size={16} />}
                    title="Phone Call"
                    description="Call using your business phone number"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3 — Choose Who Handles the Call */}
          {step === "handler" && contact && (
            <div className="space-y-4">
              <ContactSummaryCard
                contact={contact}
                badge={
                  <span className="shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800">
                    {method === "whatsapp" ? <FaWhatsapp className="text-green-500" size={12} /> : <Phone className="text-indigo-500" size={12} />}
                    {method === "whatsapp" ? "WhatsApp Call" : "Phone Call"}
                  </span>
                }
              />
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Handled by</h3>
                <div className="space-y-2.5">
                  <SelectableCard
                    selected={handlerType === "myself"}
                    onClick={() => setHandlerType("myself")}
                    icon={<Phone size={15} className="text-gray-500" />}
                    title="Call Myself"
                    description="Place the call as the logged-in agent"
                  />
                  <div>
                    <SelectableCard
                      selected={handlerType === "human"}
                      onClick={() => setHandlerType("human")}
                      icon={<UserIcon size={15} className="text-gray-500" />}
                      title="Human Agent"
                      description={handlerType === "human" ? "" : "Assign a teammate to handle this call"}
                    />
                    {handlerType === "human" && (
                      <select
                        value={humanAgentId}
                        onChange={(e) => setHumanAgentId(e.target.value)}
                        className="mt-2 w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      >
                        {HUMAN_AGENTS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    )}
                  </div>
                  <div>
                    <SelectableCard
                      selected={handlerType === "ai"}
                      onClick={() => setHandlerType("ai")}
                      icon={<Bot size={15} className="text-gray-500" />}
                      title="AI Voice Agent"
                      description={handlerType === "ai" ? "" : "Let an AI agent handle the conversation"}
                    />
                    {handlerType === "ai" && (
                      <div className="mt-2 space-y-2">
                        <select
                          value={aiAgentId}
                          onChange={(e) => setAiAgentId(e.target.value)}
                          className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {AI_AGENTS.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                        </select>
                        <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800">
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{aiAgent.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {aiAgent.tone} • {aiAgent.language} • {aiAgent.accent} • {aiAgent.mode}
                          </div>
                          <button
                            type="button"
                            onClick={() => toast("Voice settings are coming soon.")}
                            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline mt-1.5"
                          >
                            Edit voice settings
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4 — Add Call Context */}
          {step === "context" && contact && (
            <div className="space-y-4">
              <ContactSummaryCard contact={contact} />
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Call Purpose</label>
                <select
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {CALL_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Instructions for AI (Optional)</label>
                <textarea
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value.slice(0, 500))}
                  rows={4}
                  placeholder="e.g. Customer showed interest in premium plan. Share pricing and offer details."
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
                <div className="text-right text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500 mt-1">{instructions.length}/500</div>
              </div>
            </div>
          )}

          {/* Step 5 — Review and Start Call */}
          {step === "review" && contact && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold text-gray-700 shrink-0 ${contact.avatarColor}`}>{contact.initial}</span>
                  <div className="min-w-0">
                    <div className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Contact</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{contact.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{contact.phone}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <span className="w-9 h-9 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center shrink-0">
                    {method === "whatsapp" ? <FaWhatsapp className="text-green-500" size={15} /> : <Phone className="text-indigo-500" size={15} />}
                  </span>
                  <div>
                    <div className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Channel</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{method === "whatsapp" ? "WhatsApp Call" : "Phone Call"}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <span className="w-9 h-9 rounded-full bg-gray-50 dark:bg-gray-800 flex items-center justify-center shrink-0">
                    {handlerType === "ai" ? <Bot size={15} className="text-indigo-500" /> : <UserIcon size={15} className="text-gray-500" />}
                  </span>
                  <div className="min-w-0">
                    <div className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Handled by</div>
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{handlerLabel}</div>
                    {handlerType === "ai" && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{aiAgent.tone} • {aiAgent.language} • {aiAgent.accent}</div>}
                  </div>
                </div>
                <div className="p-3.5 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500">Purpose</div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{purpose}</div>
                  {instructions && (
                    <>
                      <div className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mt-2">Instructions for AI</div>
                      <div className="text-sm text-gray-700 dark:text-gray-300">{instructions}</div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-center justify-center text-center gap-4 p-6 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800">
                <span className="w-14 h-14 rounded-full bg-indigo-100 dark:bg-indigo-500/15 flex items-center justify-center">
                  <Mic size={22} className="text-indigo-600 dark:text-indigo-400" />
                </span>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Ready to call?</h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    The {handlerType === "ai" ? "AI agent" : "call"} will {handlerType === "ai" ? "call the contact and handle the conversation based on your settings." : "connect you with the contact."}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={startCall}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-sm"
                >
                  <PhoneCall size={15} /> Call Now
                </button>
                <button
                  type="button"
                  onClick={() => toast("Scheduling calls is coming soon.")}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <CalendarClock size={15} /> Schedule Call
                </button>
              </div>
            </div>
          )}

          {/* Step 6 — Live Call in Progress */}
          {step === "live" && contact && (
            <div className="bg-gray-950 rounded-lg overflow-hidden -m-4 sm:-m-6">
              <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-white/10 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white truncate">{contact.name}</div>
                  <div className="text-xs text-emerald-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Connected · {fmtTimer(seconds)}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <button type="button" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-200 bg-white/10 hover:bg-white/15 rounded-lg"><MicOff size={13} /> Mute</button>
                  <button type="button" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-200 bg-white/10 hover:bg-white/15 rounded-lg"><Pause size={13} /> Hold</button>
                  <button type="button" className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-200 bg-white/10 hover:bg-white/15 rounded-lg"><ArrowRightLeft size={13} /> Transfer</button>
                  <button type="button" onClick={endCall} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-rose-600 hover:bg-rose-700 rounded-lg">
                    <PhoneOff size={13} /> End Call
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px]">
                <div className="p-4 sm:p-5 space-y-4 max-h-[46vh] overflow-y-auto">
                  <div className="text-xs font-medium text-[#7B7B7B] font-semibold mb-1">Live Transcript</div>
                  {SAMPLE_LIVE_TRANSCRIPT.map((m, i) => (
                    <div key={i} className={`flex flex-col ${m.speaker === "agent" ? "items-start" : "items-end"}`}>
                      <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm ${m.speaker === "agent" ? "bg-white/10 text-gray-100" : "bg-indigo-600 text-white"}`}>
                        {m.text}
                      </div>
                      <span className="text-[11px] text-gray-500 mt-1 px-1">{m.speaker === "agent" ? "AI Agent" : contact.name}</span>
                    </div>
                  ))}
                </div>

                <div className="p-4 sm:p-5 border-t lg:border-t-0 lg:border-l border-white/10 space-y-4">
                  <div>
                    <div className="text-xs font-medium text-[#7B7B7B] font-semibold mb-1">Detected Intent</div>
                    <div className="text-sm text-gray-100">Product Enquiry</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-[#7B7B7B] font-semibold mb-1">Sentiment</div>
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-current" /> Positive
                    </span>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-[#7B7B7B] font-semibold mb-1">Call Summary (Live)</div>
                    <p className="text-sm text-gray-300 leading-relaxed">Customer is interested in the {purpose.toLowerCase()} and pricing details.</p>
                  </div>
                  {handlerType === "ai" && (
                    <button type="button" onClick={() => toast("You've taken over the call.")} className="w-full px-3 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg">
                      Take Over Call
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 7 — Call Completed */}
          {step === "completed" && contact && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 px-3.5 py-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-sm font-medium">
                <CheckCircle2 size={15} /> Call completed · {fmtTimer(seconds)}
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <ContactSummaryCard contact={contact} />
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                Handled by <span className="font-medium text-gray-700 dark:text-gray-300">{handlerLabel}</span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Outcome</label>
                <select
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {OUTCOMES.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Call Notes / AI Summary</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                />
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <button type="button" onClick={() => toast("Tagging is coming soon.")} className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                  <Tag size={14} /> Add Tag
                </button>
                <button type="button" onClick={() => toast("Task creation is coming soon.")} className="flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                  <ListChecks size={14} /> Create Task
                </button>
                <button type="button" onClick={saveAndClose} className="ml-auto px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-sm">
                  Save &amp; Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer continue button — steps that need an explicit "Continue" */}
        {(step === "contact" || step === "method" || step === "handler" || step === "context") && (
          <div className="px-4 sm:px-6 py-3.5 border-t border-gray-100 dark:border-gray-800 flex justify-end shrink-0">
            <button
              type="button"
              disabled={
                (step === "contact" && !contactId) ||
                (step === "method" && !method) ||
                (step === "handler" && (!handlerType))
              }
              onClick={() => {
                if (step === "contact") { setMethod(contact?.channel || "whatsapp"); setStep("method"); }
                else if (step === "method") setStep("handler");
                else if (step === "handler") setStep("context");
                else if (step === "context") setStep("review");
              }}
              className="px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors shadow-sm"
            >
              Continue →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StartCallModal;
